// Resolve the current user's tenant org id from the API-enriched Better Auth
// session. The active-org switcher still overrides this value per API request.
//
// Returns `null` until the session resolves so callers can no-op safely.

import { useAuth } from '../../lib/auth'

export function useOrgId(): string | null {
  const { session } = useAuth()
  return session?.user.user_metadata.org_id ?? null
}

/**
 * Decode a JWT body without verifying — Supabase already verified it on the
 * way in. We only want one claim. Returns `null` for any parse error or
 * missing claim so callers can fall back gracefully.
 *
 * Exported for tests.
 */
export function extractOrgIdFromJwt(jwt: string): string | null {
  try {
    const parts = jwt.split('.')
    if (parts.length < 2) return null
    const payload = parts[1]
    if (!payload) return null
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')
    const bin = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
    const json = JSON.parse(bin) as Record<string, unknown>
    const orgId = json['org_id']
    return typeof orgId === 'string' && orgId.length > 0 ? orgId : null
  } catch {
    return null
  }
}
